import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Input,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  Portal,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { MagnifyingGlass, X } from 'phosphor-react';

import { IPessoaBusca, useFinanceiroPessoas } from '../../../../hooks/useFinanceiro';
import { brlCompact, FIN, fmtDoc, RADII } from './theme';

interface EntitySearchProps {
  /** Termo atual (vem da URL via useFinanceiroFilters.q). */
  value: string;
  onChange: (q: string) => void;
  /** Escopo de franquia(s) — a busca respeita a base selecionada. */
  contaOrigem?: string;
  accent?: string;
  placeholder?: string;
}

const inicial = (nome?: string | null) =>
  (nome || '—')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(p => p[0])
    .join('')
    .toUpperCase() || '—';

/** Avatar quadrado-arredondado, tingido pela relação dominante (cliente/fornecedor). */
const Avatar = ({ p, size = 34 }: { p: IPessoaBusca; size?: number }) => {
  const cli = p.tipo === 'cliente';
  return (
    <Flex
      w={`${size}px`}
      h={`${size}px`}
      borderRadius="9px"
      flexShrink={0}
      align="center"
      justify="center"
      fontWeight={800}
      fontSize={size > 30 ? '12px' : '11px'}
      bg={cli ? '#E7F7EE' : FIN.blueSoft}
      color={cli ? FIN.pos : FIN.info}
    >
      {inicial(p.pessoa_nome)}
    </Flex>
  );
};

const TipoBadge = ({ tipo }: { tipo: string }) => {
  const cli = tipo === 'cliente';
  return (
    <Text
      as="span"
      fontSize="9.5px"
      fontWeight={800}
      letterSpacing="0.04em"
      textTransform="uppercase"
      px={1.5}
      py="1px"
      borderRadius="full"
      bg={cli ? '#E7F7EE' : FIN.blueSoft}
      color={cli ? FIN.pos : FIN.info}
      whiteSpace="nowrap"
    >
      {cli ? 'Cliente' : 'Fornecedor'}
    </Text>
  );
};

/** Metadados que a seleção "carrega": doc · ID · nº lançamentos. */
const metaLinha = (p: IPessoaBusca) =>
  [fmtDoc(p.documento), p.external_id ? `ID ${p.external_id}` : '', `${p.qtd} lanç.`]
    .filter(Boolean)
    .join('  ·  ');

export const EntitySearch = ({
  value,
  onChange,
  contaOrigem,
  accent = FIN.info,
  placeholder = 'Buscar por ID, CNPJ ou Razão Social…',
}: EntitySearchProps) => {
  const [termo, setTermo] = useState(value);
  const [termoQ, setTermoQ] = useState(value);
  const [sel, setSel] = useState<IPessoaBusca | null>(null);
  const [aberto, setAberto] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const primeira = useRef(true);

  // Debounce: alimenta a query de sugestões e o filtro global (q) da tela.
  useEffect(() => {
    if (primeira.current) {
      primeira.current = false;
      return undefined;
    }
    const id = setTimeout(() => {
      setTermoQ(termo);
      if (!sel) onChange(termo);
    }, 280);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termo]);

  const { data, isFetching } = useFinanceiroPessoas(termoQ, contaOrigem);
  const itens = data?.itens ?? [];
  const mostrar = aberto && termo.trim().length >= 2;

  const escolher = (p: IPessoaBusca) => {
    setSel(p);
    setTermo(p.pessoa_nome ?? '');
    setTermoQ(p.pessoa_nome ?? '');
    onChange(p.pessoa_nome ?? '');
    setAberto(false);
  };

  const limpar = () => {
    setSel(null);
    setTermo('');
    setTermoQ('');
    onChange('');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  // ── Estado selecionado: chip rico com os dados da entidade ───────────────
  if (sel) {
    const valor = sel.tipo === 'cliente' ? sel.total_receita : sel.total_despesa;
    return (
      <Flex
        align="center"
        gap={2.5}
        flex="1 1 280px"
        minW="240px"
        maxW="460px"
        px={2.5}
        py={1.5}
        borderRadius={RADII.pill}
        borderWidth="1px"
        borderColor={`${accent}59`}
        bg={`${accent}0D`}
      >
        <Avatar p={sel} />
        <Box flex="1" minW={0}>
          <Flex align="center" gap={2}>
            <Text fontSize="sm" fontWeight={700} color={FIN.ink} noOfLines={1}>
              {sel.pessoa_nome || '—'}
            </Text>
            <TipoBadge tipo={sel.tipo} />
          </Flex>
          <Text fontSize="11px" color={FIN.faint} noOfLines={1}>
            {metaLinha(sel)}
            {'  ·  '}
            <Text as="span" color={FIN.muted} fontWeight={600}>
              {brlCompact(valor)}
            </Text>
            {sel.em_aberto > 0 && (
              <Text as="span" color={FIN.warn} fontWeight={600}>
                {'  ·  '}
                {brlCompact(sel.em_aberto)} em aberto
              </Text>
            )}
          </Text>
        </Box>
        <Box
          as="button"
          aria-label="Limpar seleção"
          onClick={limpar}
          color={FIN.faint}
          _hover={{ color: FIN.neg }}
          flexShrink={0}
          lineHeight={0}
        >
          <X size={16} weight="bold" />
        </Box>
      </Flex>
    );
  }

  // ── Estado de busca: input + popover de sugestões ────────────────────────
  return (
    <Popover
      isOpen={mostrar}
      onClose={() => setAberto(false)}
      autoFocus={false}
      closeOnBlur
      isLazy
      placement="bottom-start"
      matchWidth
    >
      <PopoverAnchor>
        <Flex
          align="center"
          gap={2}
          flex="1 1 280px"
          minW="220px"
          maxW="460px"
          bg="white"
          px={3}
          borderRadius={RADII.pill}
          borderWidth="1px"
          borderColor={aberto ? accent : FIN.line}
          boxShadow={aberto ? `0 0 0 3px ${accent}26` : 'none'}
          transition="border-color .15s, box-shadow .15s"
        >
          <MagnifyingGlass size={16} color={FIN.faint} weight="bold" />
          <Input
            ref={inputRef}
            variant="unstyled"
            placeholder={placeholder}
            value={termo}
            onChange={e => {
              if (sel) setSel(null);
              setTermo(e.target.value);
              setAberto(true);
            }}
            onFocus={() => setAberto(true)}
            onKeyDown={e => {
              if (e.key === 'Escape') setAberto(false);
              if (e.key === 'Enter' && itens.length) escolher(itens[0]);
            }}
            py={1.5}
            fontSize="sm"
            fontWeight={500}
          />
          {isFetching && <Spinner size="xs" color={FIN.faint} flexShrink={0} />}
        </Flex>
      </PopoverAnchor>
      <Portal>
        <PopoverContent
          w="auto"
          borderRadius={RADII.card}
          overflow="hidden"
          boxShadow="0 12px 38px rgba(20,40,60,0.18)"
          border={`1px solid ${FIN.line}`}
          zIndex={1500}
          _focusVisible={{ outline: 'none' }}
        >
          <PopoverBody p={1.5}>
            {itens.length > 0 ? (
              <>
                <Flex px={2} pt={1} pb={1.5} align="center" justify="space-between">
                  <Text
                    fontSize="10px"
                    fontWeight={700}
                    letterSpacing="0.08em"
                    color={FIN.faint}
                    textTransform="uppercase"
                  >
                    {itens.length === 8
                      ? '8+ resultados'
                      : `${itens.length} resultado${itens.length > 1 ? 's' : ''}`}
                  </Text>
                  <Text fontSize="10px" color={FIN.faint}>
                    Enter seleciona o 1º
                  </Text>
                </Flex>
                <Box maxH="340px" overflowY="auto">
                  {itens.map(p => {
                    const valor = p.tipo === 'cliente' ? p.total_receita : p.total_despesa;
                    return (
                      <Flex
                        key={`${p.pessoa_nome}-${p.documento ?? ''}-${p.external_id ?? ''}`}
                        as="button"
                        w="full"
                        textAlign="left"
                        align="center"
                        gap={2.5}
                        px={2}
                        py={2}
                        borderRadius="10px"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => escolher(p)}
                        _hover={{ bg: FIN.blueSoft }}
                      >
                        <Avatar p={p} />
                        <Box flex="1" minW={0}>
                          <Text fontSize="sm" fontWeight={600} color={FIN.ink} noOfLines={1}>
                            {p.pessoa_nome || '—'}
                          </Text>
                          <Text fontSize="11px" color={FIN.faint} noOfLines={1}>
                            {metaLinha(p)}
                          </Text>
                        </Box>
                        <Box textAlign="right" flexShrink={0}>
                          <TipoBadge tipo={p.tipo} />
                          <Text
                            fontSize="xs"
                            fontWeight={700}
                            color={FIN.ink}
                            mt={0.5}
                            sx={{ fontVariantNumeric: 'tabular-nums' }}
                          >
                            {brlCompact(valor)}
                          </Text>
                        </Box>
                      </Flex>
                    );
                  })}
                </Box>
              </>
            ) : (
              <Flex direction="column" align="center" gap={1} py={5} px={3}>
                {isFetching ? (
                  <Spinner size="sm" color={accent} />
                ) : (
                  <>
                    <MagnifyingGlass size={20} color={FIN.faint} />
                    <Text fontSize="xs" color={FIN.muted} fontWeight={600}>
                      Nenhuma entidade para “{termo.trim()}”
                    </Text>
                    <Text fontSize="11px" color={FIN.faint} textAlign="center">
                      Busque por razão social, CNPJ/CPF ou ID.
                    </Text>
                  </>
                )}
              </Flex>
            )}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
